import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BrandContext = createContext(null);

const DEFAULT_BRAND = {
  business_name: "FoodCost",
  logo: "",
  primary_color: "",
  producer_name: "",
  producer_address: "",
  vat_number: "",
  contacts: "",
  label_show_production_date: true,
  label_storage_default: "",
  label_usage_default: "",
};

const hexToHsl = (hex) => {
  if (!hex || !/^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(hex)) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    hh = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hh = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hh = (b - r) / d + 2;
        break;
      default:
        hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const { user } = useAuth();

  const refresh = () =>
    api
      .get("/branding")
      .then((r) => setBrand({ ...DEFAULT_BRAND, ...r.data }))
      .catch(() => setBrand(DEFAULT_BRAND));

  useEffect(() => {
    if (user) refresh();
    else setBrand(DEFAULT_BRAND);
  }, [user]);

  useEffect(() => {
    const root = document.documentElement;
    const hsl = hexToHsl(brand.primary_color);
    if (hsl) {
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
    } else {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    }
  }, [brand.primary_color]);

  useEffect(() => {
    document.title = brand.business_name ? `${brand.business_name} — Food Cost` : "FoodCost — Controllo Food Cost";
  }, [brand.business_name]);

  return (
    <BrandContext.Provider value={{ brand, setBrand, refresh }}>{children}</BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
